<template>
  <div class="article-page__wrapper">
    <a-lazy-hydrate when-visible>
      <a-breadcrumbs
        :settings="breadcrumbsSettings"
        :prettify-names="false"
        class="click-spam-left-padding"
      />
    </a-lazy-hydrate>

    <a-in-progress :in-progress="refreshInProgress">
      <a-article
        v-if="articleData"
        v-bind="articleProps"
        class="article-page__article"
      />
    </a-in-progress>

    <a-refresh-listener
      :article.sync="articleData"
      :page-refresh-in-progress.sync="refreshInProgress"
      :fetch-data-fn="getArticleData"
      :settings="$options.consts.AUTO_REFRESH_SETTINGS.ARTICLE_PAGE"
      v-bind="refreshListenerTargetProps"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import mixins from '@/utils/mixins'
import AArticle from 'shared/AArticle'
import AInProgress from 'shared/AInProgress'
import ARefreshListener from 'shared/ARefreshListener'
import { AUTO_REFRESH_SETTINGS } from 'enums/article-auto-refresh'
import { ROUTE_NAME } from 'enums/routes'
import { findCategorySlugByArticleCategorySlug } from 'enums/categories'
import { getParsedHtmlWithReplacedImageProps } from '@/utils/helpers/replaceImageProperties'
import { DISQUS_SOURCE } from 'shared/AActionIcon/_shared/CommentCount/enums'
import { checkIfNonCanonicalPath } from '@/utils/helpers/canonical-links'
import {
  METHOD_NAME_TO_RUN_ON_LOGIN,
  METHOD_NAME_TO_RUN_ON_LOGOUT
} from '@/utils/mixins/run-method-on-login-logout'
import { METHOD_NAME, PENDING_ACTION_EVENT } from 'enums/pending-actions'
import { generateArticleUrl } from '@/utils/helpers/url-generators'
import { addAmpSuffix } from '@fmpedia/helpers'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import { pick } from 'ramda'
import { RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME } from '@/utils/enums/article'

function generateDefaultArticleContentDataModel() {
  return {
    articleData: {},
    articleSlug: '',
    categorySlug: ''
  }
}

async function getArticleData({ store, route }) {
  const { dispatch } = store
  const {
    params: { article: articleSlug, category: articleCategorySlug },
    query: { previewId, apiKey }
  } = route

  const categorySlug =
    findCategorySlugByArticleCategorySlug(articleCategorySlug) ||
    articleCategorySlug

  const [articleData, mostCommentedArticles] = await Promise.all([
    dispatch('news/requestArticleContentBySlug', {
      articleSlug,
      previewId,
      apiKey
    }),
    dispatch('news/requestMostCommentedArticles')
  ])

  const processedArticleData = {
    ...articleData,
    body: getParsedHtmlWithReplacedImageProps(
      articleData.body,
      articleData.bodyImages
    )
  }

  return {
    articleData: {
      ...processedArticleData,
      ebook: null,
      mostCommentedArticles
    },
    articleSlug,
    categorySlug
  }
}

export default {
  name: 'ArticlePage',
  validate({ params: { article } }) {
    return !!article
  },
  mixins: [
    mixins.runMethodOnLoginLogout,
    mixins.urlGenerators,
    mixins.articleScheme,
    mixins.refreshDataHandler,
    mixins.ebookDataFetchMixinFactory({ runOnMount: false }),
    mixins.disqusCommentCountMixinFactory({ source: DISQUS_SOURCE.IL }),
    mixins.pendingAuthActionsListenersFactory({
      [PENDING_ACTION_EVENT.REFRESH_SAVE_FOR_LATER]:
        METHOD_NAME.UPDATE_IS_SAVED_VALUE,
      [PENDING_ACTION_EVENT.REFRESH_FOLLOW_TAG]:
        METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE
    }),
    mixins.delayedContentRefresh
  ],
  components: {
    AArticle,
    AInProgress,
    ARefreshListener
  },
  consts: { AUTO_REFRESH_SETTINGS },
  head() {
    const thumbnailImageData = this.$_articleScheme_getThumbnailImageData(
      this.articleData
    )

    return this.$generateMetaTags({
      titleParam: this.articleData.title,
      descriptionParam: this.$helper.parseTldrJSONToText(this.articleData.tldr),
      canonicalUrl: this.canonicalUrl,
      schemes: [
        this.$_articleScheme_generateArticleScheme({
          articleData: this.articleData,
          thumbnailImageData,
          canonicalUrl: this.canonicalUrl
        }),
        ...this.$helper.generateVideoSchemasFromResponse([
          this.articleData.video,
          ...(this.articleData.bodyVideos || [])
        ])
      ],
      ogImageUrl: thumbnailImageData.url,
      additionalLinks: [
        {
          hid: 'amphtml',
          rel: 'amphtml',
          href: addAmpSuffix(this.canonicalUrl)
        }
      ]
    })
  },
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route

    try {
      const { articleData, articleSlug, categorySlug } = await getArticleData({
        store: ctx.store,
        route: ctx.route
      })

      /**
       * ToDo: Backend doesn't return article slug. Think about to create a task
       * to the Backend to fix this. Currently, the Backend article endpoint
       * works case insensitive.
       */
      const canonicalUrl = generateArticleUrl(
        {
          slug: articleData.slug || articleSlug.toLowerCase(),
          categorySlug: articleData?.category?.slug,
          isMigrated: articleData.isMigrated
        },
        ctx
      )

      if (
        !ctx.route.query.previewId &&
        checkIfNonCanonicalPath(ctx, {
          currentPath,
          canonicalUrl
        })
      ) {
        redirectHandler({ ctx, to: canonicalUrl })
        return
      }

      return {
        articleData,
        articleSlug,
        categorySlug,
        canonicalUrl
      }
    } catch (err) {
      ctx.app.$errorHandler(err, ctx, {
        showErrorPage: true,
        componentName: `Article Page - ${ctx.route.params.article}`
      })
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  data() {
    return {
      ...generateDefaultArticleContentDataModel(),
      canonicalUrl: this.$route.path,
      refreshInProgress: false
    }
  },
  computed: {
    breadcrumbsSettings() {
      const category = { slug: this.articleData.category.slug }

      return {
        [ROUTE_NAME.CATEGORY]: {
          nameHandler: () => this.articleData.category.name,
          linkHandler: () => this.generateCategoryPath(category)
        },
        [ROUTE_NAME.CATEGORY_ARTICLE]: {
          nameHandler: () => this.articleData.title
        }
      }
    },
    refreshListenerTargetProps() {
      const mainTag = this.articleData.tags.find(({ isMain }) => !!isMain)

      return {
        targetArticleId: this.articleData?.articleId,
        targetCategorySlug: this.articleData?.category?.slug,
        targetAuthorSlug: this.articleData?.author?.slug,
        targetTagSlug: mainTag?.slug
      }
    },
    articleProps() {
      if (!this.articleData) return {}

      return {
        articleId: this.articleData.articleId,
        articleSlug: this.articleSlug,
        isSaved: this.articleData.isSaved,
        isSessionWrap: this.articleData.isSessionWrap,
        isTradeCompass: this.articleData.isTradeCompass,
        category: this.articleData.category,
        title: this.articleData.title,
        tldr: this.articleData.tldr,
        author: this.articleData.author,
        sponsorshipCompany: this.articleData.sponsorshipCompany,
        publishedOn: this.articleData.publishedOn,
        body: this.articleData.body,
        ebook: this.articleData.ebook,
        tags: this.articleData.tags,
        image: this.articleData.image,
        video: this.articleData.video,
        terms: this.articleData.terms,
        tableData: this.articleData.tableData,
        isMigrated: this.articleData.isMigrated,
        mostCommentedArticles: this.articleData.mostCommentedArticles,
        canonicalUrl: this.canonicalUrl
      }
    }
  },
  methods: {
    ...mapActions({
      requestArticleContentBySlug: 'news/requestArticleContentBySlug'
    }),
    [METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE]({ id, value }) {
      this.articleData.tags = this.articleData.tags.map(tag => ({
        ...tag,
        ...(tag.id === id ? { isFollowed: value } : {})
      }))
    },
    [METHOD_NAME.UPDATE_IS_SAVED_VALUE]({ value }) {
      this.articleData.isSaved = value
    },
    [METHOD_NAME_TO_RUN_ON_LOGOUT]() {
      this.articleData.isSaved = false
      this.articleData.tags = this.articleData.tags.map(tag => ({
        ...tag,
        isFollowed: false
      }))
      this.articleData.author.isFollowed = false
    },
    async [METHOD_NAME_TO_RUN_ON_LOGIN]() {
      const payload = {
        articleSlug: this.$helper.getLastPathParam(this.$route.path),
        apiKey: this.$route.query.apiKey
      }

      const {
        isSaved = this.articleData.isSaved,
        isFollowed = this.articleData.author.isFollowed,
        tags = this.articleData.tags
      } = await this.$_refreshDataHandler_getSingleArticleEntities(payload)

      this.articleData = {
        ...this.articleData,
        isSaved,
        author: {
          ...this.articleData.author,
          isFollowed
        },
        tags
      }
    },
    async getArticleData() {
      try {
        const payload = {
          articleSlug: this.$route.params.article,
          isMigrated: this.articleData.isMigrated,
          apiKey: this.$route.query.apiKey
        }
        const articleData = await this.requestArticleContentBySlug(payload)

        return {
          ...articleData,
          body: getParsedHtmlWithReplacedImageProps(
            articleData.body,
            articleData.bodyImages
          )
        }
      } catch (err) {
        throw err
      }
    },
    async getEbookData() {
      this.articleData.ebook = await this.$_ebookDataFetch_getEbook()
    },
    refreshArticlePageContent() {
      const dataKeysForDelayedContentRefresh = Object.keys(
        generateDefaultArticleContentDataModel()
      )

      this.$_delayedContentRefresh_run({
        getOldDataFn: () => pick(dataKeysForDelayedContentRefresh, this),
        requestNewDataFn: async () => {
          const newData = await getArticleData({
            route: this.$route,
            store: this.$store
          })

          return pick(dataKeysForDelayedContentRefresh, newData)
        },
        dataComparisonKeysToOmitDeep: ['lastModified', 'ModifiedOn'],
        dataChangedCallbackFn: newData => {
          console.log('newData: ', newData)
          Object.assign(this, pick(dataKeysForDelayedContentRefresh, newData))
          this.$bus.$emit(RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME)
        }
      })
    }
  },
  mounted() {
    this.getEbookData()
    this.refreshArticlePageContent()
  }
}
</script>

<style lang="scss" scoped>
.article-page__wrapper {
  .article-page__article {
    width: 100%;
  }
}
</style>
